// © Microsoft Corporation. All rights reserved.

import { appEnvironment, InspectorArgs, InspectorResponse, getAppEnvironment, UrlUtility, IRequestContext, getContextData, headData, PageUtility } from "@msnews/core";
import { CommonHeaderConfig, NavigationItem, RenderType } from "./CommonHeader.config";
import { CommonHeaderConnectorArgs, CommonHeaderInspector } from "./CommonHeader.inspector";

import { CommonHeaderActions } from "./CommonHeader.actions";
import { CommonHeaderProps, CommonHeaderTelemetry, CommonHeaderTelemetryContext, ContextualNavItem, HamburgerMenuItem, SearchTelemetryContext } from "./CommonHeader.props";
import { CommonHeaderState } from "./CommonHeader.state";
import { ComponentConnector } from "@msnews/experiences-connector";
import { AppType, compareDisplayNameDesc, ConnectorNamespaces, windowsMarkets } from "@msnews/experiences-constants";
import { ExperienceInitialProps } from "@msnews/experiences-base";
import { PageBase, DynamicReducer, IReducer } from "@msnews/experiences-redux";
import { peregrineRouter, PeregrineRouteConfig, PeregrineRouterConfig, RouteNames, getRoutePath, CanonicalNamePathMap } from "@msnews/peregrine-router";
import { Store } from "react-redux";
import { VerticalPropertyDataConnector } from "@msnews/vertical-property-data-connector";
import { SearchFormParameters } from "@msnews/search-box";
import { CommonHeaderNavigationFailed, CommonHeaderMissingFormCode, CommonHeaderInvalidConfig, waitForTtvr } from "@msnews/diagnostics";
import { cloneDeep } from "lodash-es";
import { Telemetry, TelemetryActionType, TelemetryBehaviorType, TelemetryContentType, TelemetryType, TelemetryAttrName, TelemetryObjectBase, TelemetryMetadataAttribute } from "@msnews/telemetry-contracts";
import { TelemetryDOM } from "@msnews/telemetry-core";
import { WindowsPageSettingsActions } from "@msnews/windows-page-settings";
import { pdpServiceClient } from "@msnews/pdp-service-library";
import { isNullOrWhiteSpace } from "@microsoft/fast-web-utilities";

/** Form code name */
const FormCodeInputName = "form";

/**
 * The connector for the CommonHeader Experience.
 */
export class CommonHeaderConnector extends ComponentConnector<CommonHeaderConfig, CommonHeaderState, CommonHeaderProps, any> {
    /**
     * Use Telemetry.sendActionEvent when available (Peregrine). Otherwise, set custom event tracking function with setTrackingFunctions (Prime).
     */
    private sendActionEvent: (element?, action?, behavior?, event?, destination?, headline?) => void;

    /**
     * Use Telemetry.sendAppErrorEvent when available (Peregrine). Otherwise, set custom event tracking function with setTrackingFunctions (Prime).
     */
    private sendAppErrorEvent: (appError) => void;

    /** The vertical property data connector, if present (i.e. on a vertical page) */
    private verticalPropertyDataConnector: VerticalPropertyDataConnector;

    /** The vertical key, if it exists for the page */
    private verticalKey: string;

    /** Root url to construct full urls with nav item paths */
    private overrideRootUrl: string;

    /** Prime vertical key used to override the originally selected pivot on component connect when primePathOverride cannot be matched */
    private verticalKeyOverride: string;

    /** Prime category url segment used to override the originally selected pivot on component connect when secondary nav is enabled */
    private categoryUrlSegment: string;

    /** Contextual navigation telemetry object */
    private contextualNavTelemetry: TelemetryObjectBase;

    /** Hamburger menu telemetry object */
    private hamburgerMenuTelemetry: TelemetryObjectBase;

    /** Common header telemetry context */
    private commonHeaderTelemetryContext: CommonHeaderTelemetryContext = {};

    /** Telemetry data for the search box */
    private searchTelemetryContext: SearchTelemetryContext;

    private isContextualSecondaryNavEnabled: boolean;

    /**
     * Url segment from Prime including /{vertical}/{category}/{subcategory}. This will override the originally selected pivot if the path
     * can be matched to a route.
     */
    private primePathOverride: string;

    public setOverrideRootUrl(overrideRootUrl: string) {
        this.overrideRootUrl = overrideRootUrl;
    }

    public setVerticalKeyOverride(verticalKeyOverride: string) {
        this.verticalKeyOverride = verticalKeyOverride;
    }

    public setCategoryUrlSegment(categoryUrlSegment: string) {
        this.categoryUrlSegment = categoryUrlSegment;
    }

    public setPrimePathOverride(primePathOverride: string) {
        this.primePathOverride = primePathOverride;
    }

    public setIsContextualSecondaryNavEnabled(isContextualSecondaryNavEnabled: boolean) {
        this.isContextualSecondaryNavEnabled = isContextualSecondaryNavEnabled;
    }

    /**
     * Initializes the Common Header connector
     * @param namespace Redux subspace name for the connector
     * @param parentNamespace Redux subspace name for the parent connector
     * @param rootReducer Application root reducer
     * @param reducer The connector reducer
     * @param store The store or redux subspace that the connector will be connected to
     * @param config The common header config
     * @param requestContext The request context
     */
    public constructor(
        namespace: string,
        parentNamespace: string,
        rootReducer: DynamicReducer,
        reducer: IReducer<CommonHeaderState>,
        store: Store<any>,
        config: CommonHeaderConfig,
        requestContext: IRequestContext) {
        super(namespace, parentNamespace, rootReducer, reducer, store, config, requestContext);

        if (Telemetry) {
            this.sendActionEvent = Telemetry.sendActionEvent.bind(Telemetry);
            this.sendAppErrorEvent = Telemetry.sendAppErrorEvent.bind(Telemetry);
        }
    }

    /**
     * State mapper.
     * @param state - The current state for the connector
     */
    public mapStateToProps(state: CommonHeaderState): CommonHeaderProps {
        const connectorArgs = {
            handleRenderInfo: this.handleRenderInfo,
            goToMyFeed: this.goToMyFeed,
            goToPersonalize: this.goToPersonalize,
            updateFormParameters: this.updateFormParameters,
            sendTelemetryActionEvent: this.sendActionEvent.bind(this),
            mapContextualNavTelemetryProps: this.mapContextualNavTelemetryProps.bind(this),
            mapHamburgerMenuTelemetryProps: this.mapHamburgerMenuTelemetryProps.bind(this),
            isContextualSecondaryNavEnabled: this.isContextualSecondaryNavEnabled,
            getFormCode: this.getFormCode,
            requestId: this.getRequestId(),
            requestContext: this.requestContext,
            appErrorCallback: this.sendAppErrorEvent,
            languageUpdatedCallback: this.languageUpdatedCallback,
            searchTelemetryContext: this.searchTelemetryContext
        };

        const inspectorArgs: InspectorArgs<CommonHeaderConfig, CommonHeaderState, CommonHeaderConnectorArgs> = {
            config: this.config,
            children: this.childComponentInstanceList,
            state,
            connectorArgs
        };

        // Call the inspector to get the handled props
        const inspectorResult: InspectorResponse<CommonHeaderProps> = CommonHeaderInspector.inspect(inspectorArgs);

        const props: CommonHeaderProps = {
            ...inspectorResult.props,
            ...this.telemetryTags,
            commonHeaderTelemetryContext: this.commonHeaderTelemetryContext
        };

        return props;
    }

    /**
     * Set the tracking functions (e.g. for using Prime telemetry)
     * @param sendActionEventOverride will override sendActionEvent
     * @param sendAppErrorEventOverride will override sendAppErrorEvent
     * @param sendActionEventByMetadataOverride - function to log action using CustomEvent
     */
    public setTrackingFunctions = (
        sendActionEventOverride: (element?, action?, behavior?, event?, destination?, headline?) => void,
        sendAppErrorEventOverride: (appError) => void) => {
        this.sendActionEvent = sendActionEventOverride;
        this.sendAppErrorEvent = sendAppErrorEventOverride;
    };

    /**
     * Called before component is connected to redux store.
     * @param connectorProps - Connector props
     */
    public async onComponentConnect(connectorProps?: ExperienceInitialProps): Promise<void> {
        // Await the base class because it may be loading in child experiences, and data connectors.
        // NOTE: In the future there will be a SPA page level experience loader that will have a deterministic load order
        // and it will eliminate the need to await on the base class onComponentConnect
        await ComponentConnector.prototype.onComponentConnect.call(this, connectorProps);

        if (connectorProps && connectorProps.context) {
            if (connectorProps.context.overrideRootUrl) {
                this.setOverrideRootUrl(connectorProps.context.overrideRootUrl);
            }

            if (connectorProps.context.verticalKeyOverride) {
                this.setVerticalKeyOverride(connectorProps.context.verticalKeyOverride);
            }

            if (connectorProps.context.primePathOverride) {
                this.setPrimePathOverride(connectorProps.context.primePathOverride);
            }

            if (connectorProps.context.isContextualSecondaryNavEnabled) {
                this.setIsContextualSecondaryNavEnabled(connectorProps.context.isContextualSecondaryNavEnabled);
            }
        }

        this.searchTelemetryContext = this.createSearchTelemetryContext();
        this.commonHeaderTelemetryContext = this.createCommonHeaderTelemetryContext();

        PageBase.getInstance().rootReducer.getDataConnector(ConnectorNamespaces.VerticalPropertyData).then((dataConnector: VerticalPropertyDataConnector) => {
            this.verticalPropertyDataConnector = dataConnector;
        });

        this.verticalKey = getAppEnvironment().HostPage && getAppEnvironment().HostPage.verticalKey;

        if (this.isContextualSecondaryNavEnabled) {
            this.setSelectedPivotWithSecondaryNav(this.getRouteByPrimePathOrVerticalKeyWithSecondaryKey());
        } else {
            this.setSelectedPivot(this.getRouteByPrimePathOrVerticalKey());
        }

        waitForTtvr().then(() => {
            CommonHeaderActions.updateIsTtvrFired.getActionSender(this).send(true);
        });
    }

    /**
     * Helper to set selected pivot and register observer on router
     * @param routeOverride use this route if provided to select pivot
     */
    private setSelectedPivotWithSecondaryNav(navigationPivots: {selectedNavMenuId: string, selectedNavItemId: string, selectedSecondaryNavMenuId: string, selectedSecondaryNavItemId: string}) {
        if (isNullOrWhiteSpace(navigationPivots.selectedNavMenuId)
            && isNullOrWhiteSpace(navigationPivots.selectedNavItemId)
            && isNullOrWhiteSpace(navigationPivots.selectedSecondaryNavMenuId)
            && isNullOrWhiteSpace(navigationPivots.selectedSecondaryNavItemId)) {
            this.setSelectedPivot(this.getRouteByPrimePathOrVerticalKey());
            return;
        }

        // Get selected pivot from current route
        const currentRoute = peregrineRouter().getCurrentRoute();
        const display = currentRoute && currentRoute.display;
        CommonHeaderActions.updateSelectedTwoLevelNavItems.getActionSender(this).send(
            {id: navigationPivots.selectedNavMenuId, display},
            {id: navigationPivots.selectedNavItemId, display},
            navigationPivots.selectedSecondaryNavMenuId == null ? null : {id: navigationPivots.selectedSecondaryNavMenuId, display},
            navigationPivots.selectedSecondaryNavItemId == null ? null : {id: navigationPivots.selectedSecondaryNavItemId, display}
        );

        // Register observer to update whenever selected pivot is updated
        peregrineRouter().addSwitchListener(this.switchListener);
    }

    /**
     * Get pivot to select based on primePath and verticalKey overrides. Return undefined if none found.
     */
    private getRouteByPrimePathOrVerticalKeyWithSecondaryKey(): {selectedNavMenuId: string, selectedNavItemId: string, selectedSecondaryNavMenuId: string, selectedSecondaryNavItemId: string} {
        let selectedNavMenuId: string = null;
        let selectedNavItemId: string = null;
        let selectedSecondaryNavMenuId: string = null;
        let selectedSecondaryNavItemId: string = null;

        let selectedNavigationLevel3Id = null;
        let selectedNavigationLevel2Id = null;
        let selectedNavigationLevel1Id = null;

        if (this.verticalKeyOverride && this.config.verticalToNavItemIdMap[this.verticalKeyOverride] !== undefined) {
            if (this.primePathOverride) {
                let indexOfPathComponent = 0;
                let primePath = this.primePathOverride;
                if (primePath == "/" + this.verticalKeyOverride) {
                    return {selectedNavMenuId: this.verticalKeyOverride, selectedNavItemId: this.verticalKeyOverride, selectedSecondaryNavMenuId: null, selectedSecondaryNavItemId: null};
                }

                const pathToIdMap: Map<string, string> = new Map<string, string>();
                while (indexOfPathComponent != -1) {
                    if (primePath == "/" + this.verticalKeyOverride) {
                        if (pathToIdMap.has(primePath) == false) {
                            pathToIdMap.set(primePath, this.verticalKeyOverride);
                        }
                        if (selectedNavigationLevel3Id == null) {
                            selectedNavigationLevel3Id = this.verticalKeyOverride;
                        } else if (selectedNavigationLevel2Id == null) {
                            selectedNavigationLevel2Id = this.verticalKeyOverride;
                        } else if (selectedNavigationLevel1Id == null) {
                            selectedNavigationLevel1Id = this.verticalKeyOverride;
                        }
                    } else if (primePath && this.config.primePathToNavItemIdMap[primePath]) {
                        if (pathToIdMap.has(primePath) == false) {
                            pathToIdMap.set(primePath, this.config.primePathToNavItemIdMap[primePath]);
                        }
                        if (selectedNavigationLevel3Id == null) {
                            selectedNavigationLevel3Id = this.config.primePathToNavItemIdMap[primePath];
                        } else if (selectedNavigationLevel2Id == null) {
                            selectedNavigationLevel2Id = this.config.primePathToNavItemIdMap[primePath];
                        } else if (selectedNavigationLevel1Id == null) {
                            selectedNavigationLevel1Id = this.config.primePathToNavItemIdMap[primePath];
                        }
                    }

                    indexOfPathComponent = primePath.lastIndexOf("/");
                    primePath = primePath.substring(0, indexOfPathComponent);
                }

                selectedSecondaryNavItemId = selectedNavigationLevel3Id;
                selectedSecondaryNavMenuId = selectedNavigationLevel2Id;
                selectedNavItemId = selectedNavigationLevel2Id;
                selectedNavMenuId = selectedNavigationLevel1Id;
                if (selectedNavigationLevel1Id == null) {
                    selectedSecondaryNavMenuId = selectedNavigationLevel3Id;
                    selectedNavItemId = selectedNavigationLevel3Id;
                    selectedNavMenuId = selectedNavigationLevel2Id;
                }

                if (selectedNavMenuId != null && selectedNavMenuId != this.verticalKeyOverride) {
                    // We always want the L1 header nav menu id to match vertical
                    selectedNavItemId = selectedNavMenuId;
                    selectedNavMenuId = this.verticalKeyOverride;
                }

                const doesLastNavigationItemHaveChildren = this.doesLastNavigationItemHaveChildren(pathToIdMap);
                if (doesLastNavigationItemHaveChildren == null || (selectedNavigationLevel1Id == null && doesLastNavigationItemHaveChildren === false)) {
                    selectedSecondaryNavMenuId = null;
                    selectedSecondaryNavItemId = null;
                } else if (doesLastNavigationItemHaveChildren) {
                    selectedSecondaryNavMenuId = selectedSecondaryNavItemId;
                }

                if (selectedNavigationLevel2Id == null) {
                    return {selectedNavMenuId: null, selectedNavItemId: null, selectedSecondaryNavMenuId: null, selectedSecondaryNavItemId: null};
                }

                return {selectedNavMenuId, selectedNavItemId, selectedSecondaryNavMenuId, selectedSecondaryNavItemId};
            }

            return {selectedNavMenuId: null, selectedNavItemId: null, selectedSecondaryNavMenuId: null, selectedSecondaryNavItemId: null};
        }

        return {selectedNavMenuId: null, selectedNavItemId: null, selectedSecondaryNavMenuId: null, selectedSecondaryNavItemId: null};
    }

    /**
     * Returns true if the portion of primePath's urlSegment that determines navigation has children
     * Eg: /sports/soccer/la-liga - La-liga has children. hence this will return true
     * Eg: /sports/my-favorites - my favorites does not have children -> return false
     * Eg: /sports/nfl/teama-vs-teamb/game-center - here only the /sports/nfl determines navigation and nfl has children. returns true
     * @param pathToIdMap Mapping between url Segment to the navigation Id
     */
    private doesLastNavigationItemHaveChildren(pathToIdMap: Map<string, string>): boolean | null {
        const { navigationConfig } = this.config;
        let primePath = this.primePathOverride.substring(1); // remove the first "/"
        let indexOfsymbol = primePath.indexOf("/");
        let lastNavItemChildren = navigationConfig.contextualNavItems; // initialize to all the nav items
        let urlSegmentPath = "";
        while (primePath != null) {
            urlSegmentPath = indexOfsymbol != -1 ? urlSegmentPath + "/" + primePath.substring(0, indexOfsymbol) : urlSegmentPath + "/" + primePath;
            if (urlSegmentPath != null && pathToIdMap.has(urlSegmentPath)) {
                let navChildrenToNavigate: NavigationItem[] = null;
                if (lastNavItemChildren == null) {
                    // Ideally this should not happen, but could because of incorrect config
                    // Returning null to fall back to L1 nav only
                    return null;
                }
                lastNavItemChildren.forEach(item => {
                    if (item.id == pathToIdMap.get(urlSegmentPath)) {
                        navChildrenToNavigate = item.children;
                    }
                });

                lastNavItemChildren = navChildrenToNavigate;
                primePath = indexOfsymbol != -1 && indexOfsymbol < primePath.length ? primePath.substring(indexOfsymbol + 1) : null;
                indexOfsymbol = primePath != null ? primePath.indexOf("/") : -1;
            } else {
                indexOfsymbol = -1;
                primePath = null;
            }
        }

        return (lastNavItemChildren != null && lastNavItemChildren.length > 0);
    }

    /**
     * Get pivot to select based on primePath and verticalKey overrides. Return undefined if none found.
     */
    private getRouteByPrimePathOrVerticalKey(): string {
        if (this.primePathOverride && this.config.primePathToNavItemIdMap[this.primePathOverride] !== undefined) {
            return this.config.primePathToNavItemIdMap[this.primePathOverride];
        }

        if (this.primePathOverride) {
            let indexOfPathComponent = this.primePathOverride.lastIndexOf("/");
            let primePath = this.primePathOverride;
            while (indexOfPathComponent != -1) {
                primePath = primePath.substring(0, indexOfPathComponent);
                if (primePath && this.config.primePathToNavItemIdMap[primePath] !== undefined) {
                    return this.config.primePathToNavItemIdMap[primePath];
                }

                indexOfPathComponent = primePath.lastIndexOf("/");
            }
        }

        if (this.verticalKeyOverride && this.config.verticalToNavItemIdMap[this.verticalKeyOverride] !== undefined) {
            // If there is a vertical key override, first try to get the vertical key from the headdata. If not available, then use the passed in value
            const headDataVerticalKey = headData.ClientSettings.verticalKey;

            return (headDataVerticalKey && this.config.verticalToNavItemIdMap[headDataVerticalKey]) || this.config.verticalToNavItemIdMap[this.verticalKeyOverride];
        }
    }

    /**
     * Helper to set selected pivot and register observer on router
     * @param routeOverride use this route if provided to select pivot
     */
    private setSelectedPivot(pivotIdOverride?: string): void {
        // Get selected pivot from current route
        const currentRoute = peregrineRouter().getCurrentRoute();

        const currentSelectedPivotId = pivotIdOverride || (currentRoute && currentRoute.pivotId);
        if (currentSelectedPivotId) {
            CommonHeaderActions.updateSelectedNavItem.getActionSender(this).send({
                id: currentSelectedPivotId,
                display: currentRoute && currentRoute.display
            },
            this.isContextualSecondaryNavEnabled);
        } else if (this.config.navigationConfig.homeNavigationItem) {
            // If unsuccessful, use the home navigation item as the default
            CommonHeaderActions.updateSelectedNavItem.getActionSender(this).send(this.config.navigationConfig.homeNavigationItem);
        }

        // Register observer to update whenever selected pivot is updated
        peregrineRouter().addSwitchListener(this.switchListener);
    }

    /**
     * Listener for when route switches so common header can update the selected pivot accordingly
     * @param switchInfo information for when the route switches
     */
    private switchListener = (switchInfo) => {
        if (!switchInfo || !switchInfo.newSelection) {
            return;
        }

        const newSelectedPivotId = switchInfo.newSelection.pivotId;
        if (this.getCurrentState().selectedNavItem && this.getCurrentState().selectedNavItem.id !== newSelectedPivotId) {
            CommonHeaderActions.updateSelectedNavItem.getActionSender(this).send({
                id: newSelectedPivotId,
                display: switchInfo.newSelection.display
            });
        }
    };

    /**
     * Handler for rendering content based on the selected navigation item
     * @param id navigation item id
     * @param renderInfo information needed to render content
     * @param telemetryInfo telemetry info
     */
    private handleRenderInfo = (event: CustomEvent): void => {
        const navItem: ContextualNavItem | HamburgerMenuItem = event.detail.selectedNav;

        if (!navItem) {
            this.sendAppErrorEvent({
                ...CommonHeaderNavigationFailed,
                message: "Failed to navigate to new page, no nav item available",
                pb: {
                    ...CommonHeaderNavigationFailed.pb,
                    customMessage: `Config id: ${this.configHref}`
                }
            });
            return;
        }

        const { id, renderInfo, verticalKey } = navItem;
        if (!id || !renderInfo) {
            this.sendAppErrorEvent({
                ...CommonHeaderNavigationFailed,
                message: "Failed to navigate to new page, nav item is missing id or renderInfo",
                pb: {
                    ...CommonHeaderNavigationFailed.pb,
                    customMessage: `Config id: ${this.configHref}`
                }
            });
            return;
        }

        const shouldForceClientSideRouting = this.config.navigationConfig.shouldForceRouterForNews
            && verticalKey === "news"
            && this.verticalKey === "news"
            && appEnvironment.AppType === "hub";

        if (shouldForceClientSideRouting) {
            renderInfo.renderType = RenderType.Router;
        }
        // Prime hybrid navigating back to Peregrine
        if (renderInfo.renderType === RenderType.Router && this.overrideRootUrl && !shouldForceClientSideRouting) {
            // this.navigateWithOverrideUrl already puts a prefix on the URL, so usePrefix is set to false here
            const path = renderInfo.usePathNameAsIs ? getRoutePath(renderInfo.path, null, false) : getRoutePath(`interest/${renderInfo.path}`);
            this.navigateWithOverrideUrl(path, event);
            return;
        }

        switch (renderInfo.renderType) {
            case RenderType.Router: {
                const url = renderInfo.usePathNameAsIs ? renderInfo.path : `interest/${renderInfo.path}`;

                peregrineRouter().pushHistory(url, "", renderInfo.context);
                this.trackNavItemClick(event, url);

                // We only need to update state for this case because the other cases navigate away from the SPA
                CommonHeaderActions.updateSelectedNavItem.getActionSender(this).send(navItem);
                return;
            }

            case RenderType.ExternalLink:
                this.trackNavItemClick(event, renderInfo.externalUrl);
                window.location.href = renderInfo.externalUrl;
                return;

            case RenderType.PrimeExternalLink: {
                // We manually append the OCID because the telemetry click listener that normally appends the OCIDis bypassed when
                // we use sendActionEventByMetadata. If we aren't forwarding the OCID. then set the OCID
                const url = this.getUrlWithOcid(this.formatUrlDomain(renderInfo.externalUrl), !this.config.forwardOcidOnPrimeExternal);

                this.trackNavItemClick(event, url);
                window.location.href = url;
                return;
            }

            default:
                this.sendAppErrorEvent({
                    ...CommonHeaderNavigationFailed,
                    message: "Failed to navigate to new page, tried to navigate to item but missing renderType",
                    pb: {
                        ...CommonHeaderNavigationFailed.pb,
                        customMessage: `Config id: ${this.configHref}; Nav item id: ${id};`
                    }
                });
        }
    };

    /**
     * Logs telemetry for navigation event
     * @param event - the custom event from web component
     * @param url - the url to navigate to
     */
    private trackNavItemClick(event: CustomEvent, url: string): void {
        const navItem = event.detail.selectedNav;

        if (Telemetry) {
            const contract = TelemetryDOM.parseMetadata(navItem.telemetryMetadata);
            TelemetryDOM.setAttribute(event.target as HTMLElement, contract);
            Telemetry.sendActionEvent(event.target as HTMLElement, contract?.action || TelemetryActionType.Click, contract?.behavior || TelemetryBehaviorType.Navigate, url);
            return;
        }

        // Log using Prime tracking function
        this.sendActionEvent(null, null, null, { type: "click", target: event.target }, url, navItem.display);
    }

    /**
     * Given a url, return a new url with the ocid query param and current flights from the current window. If there is no ocid, return the given url.
     * @param url url string to add current ocid query param to
     * @param forceAppendOcid set to true if you want to explicitly set the ocid, regardless of the current ocid
     */
    private getUrlWithOcid(url: string, forceAppendOcid = false): string {
        const currentUrlSearchParams = new URLSearchParams(window.location.search);
        const currentItems = currentUrlSearchParams.getAll("item");

        if (forceAppendOcid) {
            const externalUrl = new URL(url);
            externalUrl.searchParams.set("ocid", Telemetry.getPageMetadata().contract.page.ocid);
            currentItems.forEach(item => externalUrl.search = externalUrl.search.concat(`&item=${item}`));
            return externalUrl.toString();
        }

        const ocid = currentUrlSearchParams.get("ocid");
        if (!ocid) {
            return url;
        }

        const redirectUrl = new URL(url);
        redirectUrl.searchParams.set("ocid", ocid);
        currentItems.forEach(item => redirectUrl.search = redirectUrl.search.concat(`&item=${item}`));
        return redirectUrl.toString();
    }

    /**
     * Updates url to maintain current domain
     * @param url url string to format
     */
    private formatUrlDomain(urlString: string): string {
        const url = new URL(urlString);
        const currentHostname = window.location.hostname;
        url.hostname = currentHostname;
        return url.toString();
    }

    /**
     * Method to navigate to "My Feed"
     */
    private goToMyFeed = () => {
        this.navigateToStaticPath(RouteNames.MyFeed);
    };

    /**
     * Method to navigate to "Personalize"
     */
    private goToPersonalize = () => {
        this.navigateToStaticPath(RouteNames.Personalize);
    };

    /**
     * Helper for navigating to a static path, e.g. My Feed or Personalize
     * @param event custom event
     * @param route route name
     */
    private navigateToStaticPath(route: RouteNames): void {
        if (this.overrideRootUrl) {
            this.navigateWithOverrideUrl(CanonicalNamePathMap[route]);
            return;
        }
        peregrineRouter().pushHistory(route);
    }

    /**
     * Navigates to the override url appended with the passed in path
     * @param path path to append to override url
     * @param event custom event
     */
    private navigateWithOverrideUrl(path: string, event?: CustomEvent): void {
        let overrideUrl;

        if (this.config && this.config.navigationConfig && this.config.navigationConfig.enableLocaleFeedPrefix) {
            overrideUrl = new URL("https://www.msn.com" + `/${appEnvironment.CurrentMarket}/feed${path}`);
        } else {
            overrideUrl = new URL(this.overrideRootUrl + path);
        }

        if (this.config.includePCSOnlyFlag) {
            overrideUrl.searchParams.set("pcsonly", "true");
        }

        const destinationUrl = this.getUrlWithOcid(this.formatUrlDomain(overrideUrl.toString()), false);
        if (event) {
            this.trackNavItemClick(event, destinationUrl);
        }

        // Forward ocid if available when navigating from Prime back to Peregrine
        window.location.href = destinationUrl;
    }

    /**
     * Get form code from form parameters and handles vertical and cobrand form code
     * @param formParameters Form parameters
     */
    private getFormCode = (formParameters: Array<SearchFormParameters>): string => {
        if (!formParameters || !formParameters.length) {
            this.sendAppErrorEvent({
                ...CommonHeaderMissingFormCode,
                message: "Form parameters are not configured, searchConfig is missing formParameters",
                pb: {
                    ...CommonHeaderMissingFormCode.pb,
                    customMessage: `Config id: ${this.configHref}`
                }
            });

            return "";
        }

        // Try to set the relevant form codes
        this.trySetFormCodeForVertical(formParameters, FormCodeInputName);
        this.trySetCoBrandFormCode(formParameters, FormCodeInputName);
        const formCodeItem = formParameters.find(formParameter => formParameter.name === FormCodeInputName);
        const formCode = formCodeItem ? formCodeItem.value : "";

        if (!formCode || formCode === "") {
            this.sendAppErrorEvent({
                ...CommonHeaderMissingFormCode,
                message: "Form code look up based on 'form' keyword not found in form parameters config",
                pb: {
                    ...CommonHeaderMissingFormCode.pb,
                    customMessage: `Config id: ${this.configHref}`
                }
            });
        }

        return formCode;
    };

    /**
     * Try set form code for verticals
     * @param formParameters Form parameters
     * @param input name e.g. "form"
     */
    private trySetFormCodeForVertical(formParameters: Array<SearchFormParameters>, inputName: string): void {
        if (!this.verticalPropertyDataConnector || !this.verticalKey) {
            return;
        }

        const formCode: string = this.verticalPropertyDataConnector.getSearchFormCodeForCurrentVertical(this.verticalKey);
        if (!this.populateFormParameter(formParameters, inputName, formCode)) {
            this.sendAppErrorEvent({
                ...CommonHeaderMissingFormCode,
                message: "Form code look up based on 'form/searchFormCode' keyword not found in form parameters config, failed to set form code for vertical",
                pb: {
                    ...CommonHeaderMissingFormCode.pb,
                    customMessage: `Config id: ${this.configHref}; vertical key: ${this.verticalKey};`
                }
            });
        }
    }

    /**
     * Set form code in form parameters for cobrand only when cobrand exists in qsp
     * @param formParameters Form parameters
     * @param input name e.g. "form"
     */
    private trySetCoBrandFormCode(formParameters: Array<SearchFormParameters>, inputName: string): void {
        const configFormCodes = this.config.searchBoxConfig.formCodes;
        const locationHref = getContextData(this.requestContext.data.locationHref, "locationHref");
        const coBrandParams = UrlUtility.getQueryParameterByName("cobrand", locationHref);
        if (!configFormCodes || !coBrandParams || !coBrandParams.length) {
            return;
        }

        if (!this.populateFormParameter(formParameters, inputName, configFormCodes.coBrand)) {
            this.sendAppErrorEvent({
                ...CommonHeaderMissingFormCode,
                message: "Form code look up based on 'form/searchFormCode' keyword not found in form parameters config, failed to set form code for cobrand",
                pb: {
                    ...CommonHeaderMissingFormCode.pb,
                    customMessage: `Config id: ${this.configHref}`
                }
            });
        }
    }

    /**
     * Set form code in form parameters
     * @param formParameters Form parameters
     * @param inputName name e.g. "form"
     * @param inputValue value e.g. "formCode"
     */
    private populateFormParameter(formParameters: Array<SearchFormParameters>, inputName: string, inputValue: string): boolean {
        const formParameterIndex: any = formParameters.findIndex(p => p.name === inputName);
        if (formParameterIndex === -1 || !inputValue) {
            return false;
        }

        formParameters[formParameterIndex].value = inputValue;
        return true;
    }

    private createSearchTelemetryContext(): SearchTelemetryContext {
        if (!this.telemetryObject) {
            return null;
        }

        const contract = {
            content: {
                headline: "",
                type: TelemetryContentType.TextSearch
            },
            behavior: TelemetryBehaviorType.TextSearch,
            type: TelemetryType.SearchBox
        };

        const formObject = this.telemetryObject.addOrUpdateChild({
            ...contract,
            name: CommonHeaderTelemetry.searchName
        });

        const inputObject = formObject.addOrUpdateChild({
            ...contract,
            name: CommonHeaderTelemetry.searchWebName,
            action: TelemetryActionType.Submit
        });

        const buttonObject = formObject.addOrUpdateChild({
            name: CommonHeaderTelemetry.searchInputName,
            behavior: TelemetryBehaviorType.Undefined
        });

        return {
            formObject,
            inputObject,
            buttonObject,
            formTag: formObject.render(),
            inputTag: inputObject.getMetadataTag(),
            buttonMetaTag: buttonObject.getMetadataTag()
        };
    }

    /**
     * Update the form parameters
     */
    private updateFormParameters = (): Array<SearchFormParameters> => {
        if (!this.config.searchBoxConfig || !this.config.searchBoxConfig.formParameters) {
            return [];
        }

        const updatedFormParameters = cloneDeep(this.config.searchBoxConfig.formParameters);
        const requestId = this.getRequestId();

        // "refig" parameter is passed to bing.com/search page as a query parameter to correlate search with MSN page request
        this.updateFormParameter(updatedFormParameters, "refig", requestId);
        this.updateFormParameter(updatedFormParameters, "mkt", getAppEnvironment().CurrentMarket);

        let locationHref = "";
        if (this.requestContext && this.requestContext.data && this.requestContext.data.locationHref) {
            locationHref = getContextData(this.requestContext.data.locationHref, "locationHref");
        }
        const ocid = UrlUtility.getQueryParameterByName("ocid", locationHref);
        if (ocid && !PageUtility.isDhpPage() && !PageUtility.isNtpPage()) {
            updatedFormParameters.push({value: ocid, name: "ocid"});
        }

        return updatedFormParameters;
    };

    /**
     * Update the search form input parameter
     * @param formParameters Form parameters
     * @param name Form parameter name
     * @param value Form parameter value
     */
    private updateFormParameter(formParameters: SearchFormParameters[], name: string, value: string): void {
        if (!formParameters || !value) {
            return;
        }

        const parameterIndex = formParameters.findIndex(p => p.name === name);
        parameterIndex !== -1 ?
            formParameters[parameterIndex].value = value :
            formParameters.push({ name: name, value: value });
    }

    /**
     * Gets the request id
     */
    private getRequestId(): string {
        if (Telemetry) {
            const requestIdFromTelemetry = Telemetry.getRequestId();

            if (requestIdFromTelemetry) {
                return requestIdFromTelemetry;
            }
        }

        return getAppEnvironment().ActivityId.toLowerCase();
    }

    /**
     * Callback when the market has been updated
     * @param market New market to switch to
     */
    private languageUpdatedCallback = (market: string): void => {
        pdpServiceClient.updateMarketPreference(market).then(() => {
            WindowsPageSettingsActions.marketUpdated.getGlobalActionSender().send(market);
        });
    };

    private mapNavigationItemTelemetryProps(telemetryObject: TelemetryObjectBase, navItem: NavigationItem, prefix: string): TelemetryMetadataAttribute {
        const { renderInfo } = navItem;

        return telemetryObject.addOrUpdateChild({
            name: navItem.display,
            action: TelemetryActionType.Click,
            behavior: TelemetryBehaviorType.Navigate,
            content: {
                headline: navItem.display
            },
            destinationUrl: renderInfo.usePathNameAsIs ? renderInfo.path : `interest/${renderInfo.path}`,
            feed: {
                id: navItem.id,
                name: navItem.display,
                type: "category filter"
            }
        }).getMetadata();
    }

    private mapContextualNavTelemetryProps(navItem: NavigationItem): TelemetryMetadataAttribute {
        if (!this.telemetryObject) {
            return;
        }

        return this.mapNavigationItemTelemetryProps(
            this.contextualNavTelemetry || this.telemetryObject.addOrUpdateChild({
                name: "ContextualNav",
                type: TelemetryType.Module
            }),
            navItem,
            "nav-item"
        );
    }

    private mapHamburgerMenuTelemetryProps(navItem: NavigationItem): TelemetryMetadataAttribute {
        if (!this.telemetryObject) {
            return;
        }

        return this.mapNavigationItemTelemetryProps(
            this.hamburgerMenuTelemetry || this.telemetryObject.addOrUpdateChild({
                name: "Hamburger",
                type: TelemetryType.ActionButton,
                behavior: TelemetryBehaviorType.Show,
                content: {
                    type: TelemetryContentType.Feed,
                    headline: "Hamburger"
                }
            }),
            navItem,
            "menu-item"
        );
    }

    /**
     * Creates the common header telemetry context, including the telemetry tags for the different modules
     * within the common header
     * @param parentTelemetryObject parent telemetry
     */
    private createCommonHeaderTelemetryContext(): CommonHeaderTelemetryContext {
        if (!this.telemetryObject) {
            return null;
        }

        if (!this.hamburgerMenuTelemetry) {
            this.hamburgerMenuTelemetry = this.telemetryObject.addOrUpdateChild({
                name: "Hamburger",
                type: TelemetryType.ActionButton,
                behavior: TelemetryBehaviorType.Show,
                content: {
                    type: TelemetryContentType.Feed,
                    headline: "Hamburger"
                }
            });
        }

        if (!this.contextualNavTelemetry) {
            this.contextualNavTelemetry = this.telemetryObject.addOrUpdateChild({
                name: "ContextualNav",
                type: TelemetryType.Module
            });
        }

        const closeHamburgerMetaTag = this.hamburgerMenuTelemetry.addOrUpdateChild({
            name: "CloseHamburger",
            type: TelemetryType.ActionButton,
            behavior: TelemetryBehaviorType.Close,
            content: {
                type: TelemetryContentType.Feed
            }
        }).getMetadataTag();

        const overflowMetaTag = this.contextualNavTelemetry.addOrUpdateChild({
            name: "TopNavEllipsis",
            type: TelemetryType.ActionButton,
            behavior: TelemetryBehaviorType.More,
            content: {
                type: TelemetryContentType.Feed
            }
        }).getMetadataTag();

        const logoTag = this.telemetryObject.addOrUpdateChild({ name: "MicrosoftLogo" }).render();
        const meControlTag = this.telemetryObject.addOrUpdateChild({ name: "MeControl", type: TelemetryType.ActionButton }).render();
        const settingsTelemetry = this.telemetryObject.addOrUpdateChild({
            name: "Settings",
            type: TelemetryType.ActionButton,
            behavior: TelemetryBehaviorType.Open,
            content: {
                type: TelemetryContentType.Settings
            }
        });
        const settingsMetaTag = settingsTelemetry.getMetadataTag();
        const settingsPersonalizeButtonMetaTag = settingsTelemetry.addOrUpdateChild({
            name: "SettingsPersonalizeButton",
            type: TelemetryType.ActionButton,
            content: {
                type: TelemetryContentType.Settings
            }
        }).getMetadataTag();

        const settingsCloseButtonMetaTag = settingsTelemetry.addOrUpdateChild({
            name: "Close",
            type: TelemetryType.ActionButton,
            behavior: TelemetryBehaviorType.Close,
            content: {
                type: TelemetryContentType.Settings
            }
        }).getMetadataTag();

        const settingsLanguageSelectorTelemetry = settingsTelemetry.addOrUpdateChild({
            name: "MarketSelector",
            type: TelemetryType.ActionButton,
            behavior: TelemetryBehaviorType.Customize,
            content: {
                type: TelemetryContentType.Settings,
                headline: "MarketSelectorOptions"
            }
        });
        const settingsLanguageSelectorTag = settingsLanguageSelectorTelemetry.render();
        const languageOptions = windowsMarkets.sort(compareDisplayNameDesc).map(market => ({
            market,
            telemetryTag: settingsLanguageSelectorTelemetry.addOrUpdateChild({
                name: "MarketSelector",
                behavior: TelemetryBehaviorType.Customize,
                type: TelemetryType.ActionButton,
                content: {
                    type: TelemetryContentType.Settings,
                    headline: market.displayString
                }
            }).render()
        }));

        const interestsNavOptionsMetadata = this.hamburgerMenuTelemetry.addOrUpdateChild({
            name: "InterestsNavOptions",
            action: TelemetryActionType.Click,
            behavior: TelemetryBehaviorType.Show,
            content: {
                type: TelemetryContentType.Feed,
                headline: "Interests Nav Options"
            }
        });
        const hamburgerMenuExpandedChangeTelemetryCallback = (event: CustomEvent) => {
            const hamburgerMenuItem = event.detail;
            if (!hamburgerMenuItem) {
                return;
            }

            if (Telemetry) {
                TelemetryDOM.setAttribute(hamburgerMenuItem as HTMLElement, null, interestsNavOptionsMetadata);
                Telemetry.sendActionEvent(hamburgerMenuItem, TelemetryActionType.Click, TelemetryBehaviorType.Show);
                return;
            }

            // Log using Prime tracking function
            this.sendActionEvent(null, null, null, { type: "click", target: hamburgerMenuItem }, null, "Interests Nav Options");
        };

        return {
            hamburgerMenuTelemetry: this.hamburgerMenuTelemetry,
            contextualNavTelemetry: this.contextualNavTelemetry,
            overflowMetaTag,
            closeHamburgerMetaTag,
            logoTag,
            meControlTag,
            settingsMetaTag,
            settingsPersonalizeButtonMetaTag,
            settingsCloseButtonMetaTag,
            settingsLanguageSelectorTag,
            hamburgerMenuExpandedChangeTelemetryCallback,
            languageOptions
        };
    }
}

/**
 * Get peregrine router config from common header config
 * @param config common header navigation config
 */
export function getPeregrineRouterConfig(config: CommonHeaderConfig): PeregrineRouterConfig {
    if (!config || !config.navigationConfig) {
        Telemetry.sendAppErrorEvent({
            ...CommonHeaderInvalidConfig,
            message: "Config is missing or config is missing navigationConfig"
        });
        return null;
    }

    const { navigationConfig } = config;
    if (!navigationConfig.hamburgerMenuItems
        || !navigationConfig.hamburgerMenuItems.length
        || !navigationConfig.homeNavigationItem
        || !navigationConfig.contextualNavItems
        || !navigationConfig.contextualNavItems.length) {
        Telemetry.sendAppErrorEvent({
            ...CommonHeaderInvalidConfig,
            message: "Config is missing nav items. hamburgerMenuItems, homeNavigationItem, and/or contextualNavItems is misconfigured"
        });
        return;
    }

    const { hamburgerMenuItems, homeNavigationItem, contextualNavItems, interestsNavigationItem } = navigationConfig;
    const routes: PeregrineRouteConfig[] = [];

    // Add routes from the hamburger menu
    hamburgerMenuItems.forEach(menuItem => {
        routes.push(convertNavigationItemToPeregrineRoute(menuItem));

        if (menuItem.children && menuItem.children.length) {
            menuItem.children.map(child => routes.push(convertNavigationItemToPeregrineRoute(child)));
        }
    });

    // Add routes from the contextual nav, while not duplicating any routes
    tryPushToRoutes(routes, homeNavigationItem);
    contextualNavItems.forEach(item => {
        tryPushToRoutes(routes, item);

        if (item.children && item.children.length) {
            item.children.forEach(child => {
                tryPushToRoutes(routes, child);

                if (child.children && child.children.length) {
                    child.children.map(grandchild => tryPushToRoutes(routes, grandchild));
                }
            });
        }
    });

    // Add routes from the interests nav item if it exists, while not duplicating any routes
    tryPushToRoutes(routes, interestsNavigationItem);
    if (interestsNavigationItem && interestsNavigationItem.children && interestsNavigationItem.children.length) {
        interestsNavigationItem.children.forEach(child => {
            tryPushToRoutes(routes, child);
        });
    }

    return {
        staticRoutes: routes,
        disableLoadingDynamicRoutes: true,
        enableLocaleFeedPrefix: config.navigationConfig.enableLocaleFeedPrefix
    };
}

/**
 * Method to push a navigation item to the routes array, given that it isn't already part of the array
 * @param routes array of routes to push to
 * @param item item trying to push to array
 */
export function tryPushToRoutes(routes: PeregrineRouteConfig[], item: NavigationItem): void {
    if (!routes || !routes.length || !item || routes.some(route => route && route.pivotId === item.id)) {
        return;
    }
    routes.push(convertNavigationItemToPeregrineRoute(item));
}

/**
 * Converts navigation item to peregrine route
 * @param item navigation item
 */
export function convertNavigationItemToPeregrineRoute(item: NavigationItem): PeregrineRouteConfig {
    if (!item || !item.renderInfo) {
        return;
    }

    // Create experience info based on whether it's a git config or cms config
    let experienceInfo;
    if (item.renderInfo.experienceConfigHref) {
        experienceInfo = {
            href: item.renderInfo.experienceConfigHref,
            instanceId: item.id
        };
    } else if (item.renderInfo.experienceConfigRef) {
        experienceInfo = {
            ...item.renderInfo.experienceConfigRef,
            instanceId: item.id
        };
    }

    return {
        externalUrl: item.renderInfo.externalUrl,
        experienceInfo,
        canonicalName: item.renderInfo.usePathNameAsIs ? item.renderInfo.path : `interest/${item.renderInfo.path}`,
        telemetryPageName: item.telemetryInfo && item.telemetryInfo.pageName,
        telemetryPivotCategory: item.telemetryInfo && item.telemetryInfo.pivotCategory,
        pivotId: item.id,
        context: item.renderInfo.context,
        display: item.display,
        categoryKey: item.categoryKey,
        subCategoryKey: item.subCategoryKey,
        pageTitle: item.pageTitle
    };
}
